// The catalog editor state the store product sections share.
//
// Every rich tab of the product dialog (Information, Images, Content, License
// Keys …) is one of the old catalog editor tabs, and they all read and write
// through one `useProductEditor` — the catalog's `book {load}` / `book {save}`
// actions. So the extensions declare this one provider (they all point at the
// same component, which the registry mounts once, above the tab switch): it
// holds the editor plus the lists the tabs expect, and hands the product
// dialog its dirty state and save, so the dialog's Save writes the catalog
// document with the store product.
//
// Only a catalog product has such a document. The store also sells coins
// packs, gift cards and subscriptions, and for those the provider loads
// nothing and the tabs are not offered at all (see `catalogVisible`).
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import Tabs from "aldoo-ra/Tabs"
import { showNotification } from "aldoo-ra/Notification"
import { useVendorAccess } from "../../../AdminShared/VendorContext"
import {
  ListAssignableCategories,
  ListSupportedPlatforms,
  ListLicenseKeys,
  SupportedCurrencies,
  RentSubscriptions,
  TrailerChanged,
} from "../../../AdminShared/api"
import { errorMessage } from "../../../AdminShared/ui"
import { useAdminLanguages } from "../../../AdminShared/languages"
import { useProductEditor } from "./useProductEditor"
import { useEditingLanguage } from "aldoo-ra/Admin/Store/Products/EditingLanguage"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { fieldVisible, tabVisibility, getMinimumAmount, formatMinimumAmount, EPublishStatus } from "./editor-config"
import { Snackbar } from "./shared"
import { useStoreCurrency } from "aldoo-ra/Store/currency"
import { useContentTypes, useLoadedContentTypeId, publishContentTypeId } from "./content-types"
import { useProductTypes, profileIdOf } from "../../../AdminShared/product-types/useProductTypes"
import { productMethodsValid } from "../shipping/DeliveryMethods"
import { noticeSentForReview } from "./review-notice"

// The store category a catalog product is in; everything else the store sells
// (coins packs, gift cards, subscriptions) has no catalog document.
const CATALOG_CATEGORY = "aldoo-book"

const CatalogEditorContext = createContext(null)

/** The shared catalog editor of the product dialog, or null outside it. */
export const useCatalogEditor = () => useContext(CatalogEditorContext)

/** A CMS text value is a string or a { value } option */
const plain = (value) => (value && typeof value === "object" ? value.value : value)

/** Is this store product a catalog product? */
export const isCatalogProduct = (product) => plain(product?.category) === CATALOG_CATEGORY

const notify = (message, isError = false) =>
  showNotification({ message, className: isError ? "bg-admin_error text-white" : "bg-admin_success text-white" })

/**
 * The extra context the catalog tab rules are asked with. The CMS evaluates
 * visibility above the editor, so this hook — declared by every catalog
 * extension, mounted once — is what loads the content type the rules need.
 */
export function useCatalogVisibilityContext({ product }) {
  const contentTypes = useContentTypes()
  const { bySlug } = useProductTypes()
  // what the editor read once it is open; before that the product's type in the
  // tree answers it — the profile of the type or of the nearest one above it
  const loadedContentTypeId = useLoadedContentTypeId(product?.pid)
  const contentTypeId = loadedContentTypeId ?? (profileIdOf(bySlug, plain(product?.productType)) || undefined)
  const contentType = useMemo(
    () => contentTypes.find((entry) => entry.contentId === contentTypeId) || null,
    [contentTypes, contentTypeId]
  )
  return useMemo(
    () => ({
      catalogProduct: isCatalogProduct(product),
      // approved and on sale: what only a published product has (its buyers' feedback)
      published: Number(product?.publishStatus) === EPublishStatus.Published,
      // null until the content type is known: the rules then show every tab
      catalogVisibility: contentType ? tabVisibility(contentType) : null,
      // the content type's store extensions: the framework offers a tab only
      // when its id is listed (aldoo-ra/Admin/Store/extensions); empty or
      // missing — a type the migration has not filled — leaves it to the
      // rules below, as before
      storeExtensions: Array.isArray(contentType?.extensions) && contentType.extensions.length ? contentType.extensions : null,
    }),
    [product, contentType]
  )
}

/**
 * The `visible` rule of a catalog tab: it shows for a catalog product whose
 * content type uses at least one of `tabKeys` (the keys of `tabVisibility`).
 * Once the content type lists its store extensions, the list decides which
 * tabs a product has (the framework applies it before this rule), and the
 * keys only matter for a type that has no list yet.
 */
export const catalogVisible = (...tabKeys) => (context) => {
  if (!context?.catalogProduct) return false
  if (Array.isArray(context.storeExtensions) && context.storeExtensions.length) return true
  // the content type answers which tabs a product uses; until it is known the
  // tab shows — a product that opens with no catalog tab at all is worse than
  // one tab too many
  if (!context.catalogVisibility) return true
  return tabKeys.some((key) => context.catalogVisibility?.[key])
}

/** License keys state shared by the Availability and License Keys tabs */
function usePlk(pid, enabled) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState("")
  const [platformFilter, setPlatformFilter] = useState("")
  const [search, setSearch] = useState("")
  const load = useCallback(
    async (nextPage = 1, state = filter, text = search, platformId = platformFilter) => {
      if (!pid || !enabled) return
      setLoading(true)
      try {
        const res = await ListLicenseKeys({ pid, state: state || undefined, page: nextPage, pageSize: 50, search: text || undefined, platformId: platformId || undefined })
        if (errorMessage(res)) return
        setData(res)
        setPage(nextPage)
      } finally {
        setLoading(false)
      }
    },
    [pid, enabled, filter, search, platformFilter]
  )
  useEffect(() => {
    if (enabled) load(1)
  }, [pid, enabled])
  return { data, loading, page, filter, setFilter, platformFilter, setPlatformFilter, search, setSearch, load }
}

/**
 * Mounted by the CMS content editor for every enabled section extension.
 * A product without a pid has no catalog document behind it yet, and a
 * product of another store category never will, so neither asks the server
 * for anything.
 */
export function CatalogEditorProvider({ dataItem, register, children }) {
  if (!dataItem?.pid || !isCatalogProduct(dataItem)) return children
  return (
    <LoadedCatalogEditor pid={dataItem.pid} register={register}>
      {children}
    </LoadedCatalogEditor>
  )
}

function LoadedCatalogEditor({ pid, register, children }) {
  // the store admin is the framework's own page and mounts no VendorProvider,
  // so the access is resolved here when there is none above
  const { isAdmin, org } = useVendorAccess()
  // the language picked at the top of the product dialog, which every tab follows
  const editingLanguage = useEditingLanguage()
  const editor = useProductEditor(pid, editingLanguage)
  // the dialog opens on the site's primary language before this document is read: a product
  // made in another (a Bulgarian shop's) is then shown in its own, once
  const openedIn = useRef(null)
  useEffect(() => {
    const own = editor.product?.languages?.[0]
    if (!editingLanguage || !own || openedIn.current === pid) return
    openedIn.current = pid
    if (editingLanguage.language === editingLanguage.primary && own !== editingLanguage.primary && editingLanguage.languages.some((l) => l.code === own)) editingLanguage.setLanguage(own)
  }, [pid, editor.product, editingLanguage])
  const languages = useAdminLanguages()
  const contentTypes = useContentTypes()
  const [categories, setCategories] = useState(null)
  // the whole taxonomy of the catalog in tree order (the Categories tab's
  // picker), and every category's name by id
  const [categoryTree, setCategoryTree] = useState([])
  const [categoryNames, setCategoryNames] = useState({})
  const [platforms, setPlatforms] = useState([])
  // the ones the API lists; until it answers, the store's own (its config)
  const { currencies: storeCurrencies } = useStoreCurrency()
  const [listedCurrencies, setCurrencies] = useState(null)
  const currencies = useMemo(() => listedCurrencies || storeCurrencies.map((code) => ({ code })), [listedCurrencies, storeCurrencies])
  const [rentSubscriptions, setRentSubscriptions] = useState([])
  const [uploadError, setUploadError] = useState("")
  const [validationError, setValidationError] = useState("")
  const t = useProductEditorTexts()

  useEffect(() => {
    // in the shop's language, one for all: the picker lists and searches what
    // the shop shows (a category with no Bulgarian name comes in English);
    // the series offered are everyone's and this product's organization's
    ListAssignableCategories("bg", { all: true, pid })
      .then((res) => {
        const tree = res?.list || []
        // the assignable leaves are what the series picker offers
        setCategories(tree.filter((category) => category.leaf))
        setCategoryTree(tree)
        setCategoryNames(res?.names || {})
      })
      .catch(() => setCategories([]))
    ListSupportedPlatforms().then((res) => setPlatforms(Array.isArray(res) ? res : res?.list || [])).catch(() => {})
    SupportedCurrencies().then((res) => res?.currencies?.length && setCurrencies(res.currencies)).catch(() => {})
    RentSubscriptions().then((res) => setRentSubscriptions(res?.list || [])).catch(() => {})
  }, [pid])

  // a category made while the dialog is open (a series the vendor named): listed at once, at
  // the end (the next load files it in its place in the tree)
  const addCategory = useCallback((category) => {
    if (!category?._id) return
    const add = (list) => (list || []).some((c) => c._id === category._id) ? list : [...(list || []), category]
    if (category.leaf !== false) setCategories(add)
    setCategoryTree(add)
    setCategoryNames((names) => ({ ...names, [category._id]: category.name }))
  }, [])

  // every editor of the organization edits; approval is the platform admin's
  const isProductAdmin = true
  const isProductEditor = true

  // the product's profile, as the raw load answers it from its type in the tree
  const contentTypeId = editor.read("profile")?.id
  const contentType = useMemo(() => contentTypes.find((ct) => ct.contentId === contentTypeId) || null, [contentTypes, contentTypeId])
  const shouldShowField = useCallback((field) => fieldVisible(contentType, field), [contentType])
  const visibility = useMemo(() => tabVisibility(contentType), [contentType])
  const plk = usePlk(pid, !!contentType?.isLicenseKey)

  // the tab rules are evaluated outside this provider: tell them what we read
  useEffect(() => {
    publishContentTypeId(pid, contentTypeId)
  }, [pid, contentTypeId])

  const save = useCallback(async () => {
    // the publish checks of the old editor frame, now part of the save itself
    const publishStatus = editor.read("publishStatus")
    const publishing = publishStatus === EPublishStatus.InReview || publishStatus === EPublishStatus.Published

    // a product that ships must have a weight before it is published
    if (shouldShowField("weight") && publishing && !editor.read("free_shipping")) {
      const fieldsId = editor.product?.fields
      const entry = fieldsId ? editor.loc[typeof fieldsId === "object" ? fieldsId?._id : fieldsId] : null
      const hasWeight = Object.values(entry?.translations || {}).some((map) => parseFloat(map?.weight) > 0)
      if (!hasWeight) {
        setValidationError(t`Products with shipping must have a weight greater than 0 before publishing, or enable free shipping.`)
        return false
      }
    }

    // a product with delivery methods of its own keeps at least one the company ships with
    if (!productMethodsValid(editor.read("shippingMethods"), editor.read("companyShipping"))) {
      setValidationError(t`Choose at least one delivery method for this product.`)
      return false
    }

    // the price must meet the payment processor's minimum (free products aside)
    const cost = editor.read("cost")
    if (cost && !editor.read("free_access")) {
      const [currency, cents] = String(cost).split("_")
      if (Number(cents) > 0 && Number(cents) < getMinimumAmount(currency)) {
        setValidationError(t("Price must be at least %s %s to meet payment processor requirements.", formatMinimumAmount(currency), currency))
        return false
      }
    }

    let edited
    try {
      edited = await editor.save()
    } catch (e) {
      notify(e.message || t`Save failed`, true)
      return false
    }
    // a change a buyer sees, on a product on sale: back to review, said once
    if (editor.takeSentForReview()) noticeSentForReview({ sentForReview: true }, t, editor)
    // the server re-reads a changed trailer
    const trailer = editor.read("bookTrailer")
    if (trailer) TrailerChanged(pid, trailer).catch(() => {})
    // what was edited here goes back to the dialog's form, which saves next
    return edited && typeof edited === "object" ? edited : true
  }, [editor, pid, shouldShowField, t])

  useEffect(() => {
    register?.("catalog", { dirty: editor.dirty, save, changes: editor.changes })
    return () => register?.("catalog", null)
  }, [register, editor.dirty, save, editor.changes])

  const panelProps = useMemo(
    () => ({
      pid,
      editor,
      shouldShowField,
      contentType,
      contentTypes,
      isAdmin,
      isProductAdmin,
      isProductEditor,
      languages,
      currencies,
      rentSubscriptions,
      categories,
      categoryTree,
      categoryNames,
      addCategory,
      platforms,
      plk,
      plkData: plk.data,
      reloadPlk: () => plk.load(plk.page),
      companyDeliveryDays: org?.deliveryDays,
      companyReturnDays: org?.returnDays,
      companyShipsTo: org?.shipsTo,
      setUploadError,
    }),
    [pid, editor, shouldShowField, contentType, contentTypes, isAdmin, languages, currencies, rentSubscriptions, categories, categoryTree, categoryNames, addCategory, platforms, plk, org]
  )

  const value = useMemo(
    () => ({ pid, editor, isAdmin, isProductAdmin, isProductEditor, languages, ready: editor.ready, error: editor.error, visibility, panelProps }),
    [pid, editor, isAdmin, languages, editor.ready, editor.error, visibility, panelProps]
  )

  return (
    <CatalogEditorContext.Provider value={value}>
      <Snackbar message={uploadError} onClose={() => setUploadError("")} />
      <Snackbar message={validationError} onClose={() => setValidationError("")} />
      {children}
    </CatalogEditorContext.Provider>
  )
}

const Notice = ({ children }) => <div className="text-admin_text_secondary text-sm p-2">{children}</div>

/**
 * A product dialog section that renders one of the catalog editor tabs.
 * The tab is only offered when the content type uses it (`catalogVisible`), so
 * here there is nothing left to check but the loading of the document.
 */
export const catalogSection = (Tab, extraProps) =>
  function CatalogSection({ renderField, onChange, close, value, extendedFields }) {
    const catalog = useCatalogEditor()
    const t = useProductEditorTexts()
    if (!catalog) return <Notice>{t`This product has no catalog document yet.`}</Notice>
    if (catalog.error) return <Notice>{catalog.error}</Notice>
    if (!catalog.ready) return <Notice>{t`Loading…`}</Notice>
    // `syncValues` patches the dialog's own field values. A tab needs it for a
    // field the store product carries too: the sections save first and the
    // dialog then writes the whole document, so a value only set here would be
    // written back over with the one the dialog still holds.
    // `storeValues` / `extendedFields`: the dialog's own values, and the fields other extensions put
    // into this tab (a tab that places them itself, `placesExtendedFields`)
    return <Tab {...catalog.panelProps} {...extraProps} renderField={renderField} syncValues={onChange} close={close} storeValues={value} extendedFields={extendedFields} />
  }

/**
 * A section that holds more than one catalog tab — the store product model has
 * fewer extensions than the catalog editor had tabs, so an extension that owns
 * the fields of two of them shows both, switched inside the panel.
 * `tabs` is [{ key, label, Tab, props }], `key` being the tabVisibility key:
 * the section is offered when any of them applies, and shows those that do.
 */
export const catalogTabsSection = (tabs) =>
  function CatalogTabsSection() {
    const catalog = useCatalogEditor()
    const [active, setActive] = useState(tabs[0].key)
    const t = useProductEditorTexts()
    if (!catalog) return <Notice>{t`This product has no catalog document yet.`}</Notice>
    if (catalog.error) return <Notice>{catalog.error}</Notice>
    if (!catalog.ready) return <Notice>{t`Loading…`}</Notice>

    const visible = tabs.filter((tab) => catalog.visibility[tab.key])
    if (!visible.length) return <Notice>{t`This product type does not use this tab.`}</Notice>

    const current = visible.find((tab) => tab.key === active) || visible[0]
    return (
      <div className="flex flex-col gap-4 w-full">
        {visible.length > 1 && (
          <Tabs
            variant="admin"
            value={current.key}
            onChange={setActive}
            tabs={visible.map((tab) => ({ value: tab.key, label: t(tab.label) }))}
          />
        )}
        <current.Tab {...catalog.panelProps} {...(current.props || {})} />
      </div>
    )
  }
